<template>
  <ux-organismes-restaurants-view
    :label="capitalizedCity"
    :search-restaurants-results="restaurantResults"
    :view="RestaurantsView.Map"
    @filter-change="handleFilterChange"
    page-name="restaurantsandbars::results::map"
    v-if="restaurantResults"
  />
</template>
<script lang="ts" setup>
import { RestaurantsView } from '~/core';
import { useCityAsyncData, useSearchRestaurantsAsyncData } from '~/graphql';
import { capitalizeFirstLetter } from '~/helpers/capitaliseFirstLetter';
import { daysDifference } from '~/helpers/date';
import { gtmPageAndUser } from '~/helpers/gtm';
import { sanitizeString } from '~/helpers/sanitizeString';

useRobotsRule(true);

definePageMeta({
  layout: 'map'
});

const route = useRoute();
const { baseCountry } = useCountry();
const { locale, t } = useI18n();
const config = useRuntimeConfig();

const city = computed(() => (route.params.city ? (route.params.city as string).toLowerCase() : ''));
const groupSize = computed(() => parseInt(route.query.guests as string));
const date = computed(() => route.query.date as string);
const country = computed(() => (route.params.country ? (route.params.country as string).toLowerCase() : ''));
const userStore = useUserStore();

baseCountry.value = country.value;

const { handleFilterChange, initialFilterInput, searchParams } = useSearchRestaurant({
  citySlug: city.value,
  countrySlug: country.value,
  date,
  groupSize
});

const { data } = await useSearchRestaurantsAsyncData({
  ...searchParams.value,
  citySlug: city.value,
  countrySlug: country.value,
  searchFilters: { ...initialFilterInput(), hotelCluster: true, includeFilters: true }
});
const { data: cityData, error } = await useCityAsyncData({ citySlug: city.value, countrySlug: country.value });

const capitalizedCity = computed(() => sanitizeString(capitalizeFirstLetter(cityData.value?.city?.mainTitle ?? city.value)));

useAsyncDataError(error, 'city-slug-map');

useRedirectIfEmpty(cityData.value?.city);

const restaurantResults = computed({
  get: () => data?.value?.searchRestaurants,
  set: (value) => value
});

const setI18nParams = useSetI18nParams();

setI18nParams({
  en: { city: cityData.value?.city?.translatedSlug?.en, country: cityData.value?.city?.translatedCountrySlug?.en },
  fr: { city: cityData.value?.city?.translatedSlug?.fr, country: cityData.value?.city?.translatedCountrySlug?.fr }
});

const pageEvent = () => {
  gtmPageAndUser({
    brandcontext: 'restaurantsandbars',
    category: 'restaurantsandbars',
    countrymarket: 'fr',
    currencycode: 'EUR',
    daysbeforearrival: route?.query?.date ? daysDifference(route?.query.date as string) : 0,
    dynamic_datas_ready: true,
    event: 'push_dynamic_datas',
    numberofresults: restaurantResults.value?.results ? restaurantResults.value?.results?.length : 0,
    pagename: 'restaurantsandbars::results::map',
    pagetype: 'restaurantsandbars',
    sub_category_level1: 'results',
    sub_category_level2: 'map'
  });
};

useHead(() => ({
  link: [{ href: `${config.public.baseUrl}/${locale.value}/${country.value}/${city.value}/map`, rel: 'canonical' }],

  meta: [
    {
      content:
        !restaurantResults?.value?.results || restaurantResults.value?.results?.length < 2
          ? 'noindex'
          : cityData.value?.city?.metaDescription ?? t('pages.city.description', { cityName: capitalizedCity.value }),
      name: 'description'
    },
    {
      content: !restaurantResults?.value?.results || restaurantResults.value?.results?.length < 2 ? 'noindex' : 'index',
      name: 'robots'
    }
  ],
  title: cityData.value?.city?.metaTitle ?? t('pages.city.title', { cityName: capitalizedCity.value })
}));

watch(
  () => searchParams.value,
  async () => {
    const { data } = await useSearchRestaurantsAsyncData({
      ...searchParams.value,
      citySlug: city.value,
      countrySlug: country.value,
      searchFilters: { ...initialFilterInput(), hotelCluster: true, includeFilters: true }
    });
    restaurantResults.value = data?.value?.searchRestaurants;
  }
);

whenever(
  () => userStore.userLoadDone,
  () => {
    pageEvent();
  }
);

onMounted(() => {
  if (userStore.userLoadDone) {
    pageEvent();
  }
});
</script>
